/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Component, OnInit } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { BsModalRef, BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import { AccountUsageTypes, Merchant, MerchantMigrationDetails, VendorTypes } from "../../../models";
import { AuthService, MerchantService, NotificationService, SharedFormService } from "../../../services";
import { MERCHANT_CONSTANTS, PATTERN_CONSTANTS } from "../../../utils/data.rest.constants";
import { accountNumberValidation, minSelected, requiredTrimmed } from "../../../validators/MPCustomValidator";
import { ConfirmationModalComponent } from "./confirmation-modal/confirmation-modal.component";

@Component({
  selector: "app-create-merchant",
  templateUrl: "./create-merchant.component.html"
})
export class CreateMerchantComponent implements OnInit {
  createMerchant!: FormGroup;
  merchant!: Merchant;
  vendorType = VendorTypes;
  singleVendorType = [
    { key: this.vendorType.INSTAMED, value: this.vendorType.INSTAMED },
    { key: this.vendorType.GLOBALPAYMENTS, value: this.vendorType.GLOBALPAYMENTS }
  ];
  accountUsage = AccountUsageTypes;
  faPlus = faPlus;
  faTrash = faTrash;
  hasRemit = false;
  updateMode = false;
  actionInProgress = false;
  ownerships!: string[];

  public modalRef!: BsModalRef;

  constructor(
    public merchantService: MerchantService,
    private authService: AuthService,
    private sharedFormService: SharedFormService,
    private notificationService: NotificationService,
    private router: Router,
    private fb: FormBuilder,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    this.merchant = {
      id: 0,
      merchantName: "",
      merchantLegalName: "",
      doingBusinessAs: "",
      externalMerchantId: 0,
      merchantAddress: {
        address1: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        zip: ""
      },
      acceptedCreditCards: [],
      phoneNumber: "",
      maxTransactionLimit: 0,
      accountDetails: {
        accountNumber: "",
        accountType: "",
        amexSid: "",
        checkingDepositType: "",
        federalTaxId: "",
        instaMedAccountDetails: {
          clientId: "",
          clientSecret: "",
          instaMedTerminalIDList: {},
          merchantId: "",
          storeId: "",
          accountId: ""
        },
        globalPaymentsAccountDetails: {
          globalPaymentsMerchantName: "",
          globalPaymentsSiteId: "",
          globalPaymentsKey: ""
        },
        paypalAccountDetails: {
          paypalCardNotPresentPassword: "",
          paypalCardNotPresentUsername: "",
          paypalCardPresentPassword: "",
          paypalCardPresentUsername: "",
          paypalPartner: ""
        },
        preferredProcessor: this.vendorType.INSTAMED,
        qbPayAccountDetails: null as any,
        routingNumber: "",
        separateFundingAccounts: false,
        worldPayAccountDetails: null as any,
        feeAccountDetails: {
          accountNumber: "",
          accountType: "C",
          routingNumber: ""
        }
      },
      contractedRates: null as any,
      status: null as any,
      deactivatedDate: new Date(),
      customerAccountNumber: "",
      alsoKnownAsName: "",
      primaryContactFirstName: "",
      primaryContactLastName: "",
      primaryContactEmail: "",
      primaryContactPhoneNumber: "",
      legalEntityAddress: {
        address1: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        zip: ""
      },
      legalEntityPhoneNumber: "",
      payAPICustomer: {
        customerName: "NG Pay",
        id: 0
      },
      merchantAttributes: [],
      notifiedPayCustomerProvisioning: false,
      fraudVars: {
        duplicateDollarTxCount: 0,
        duplicateDollarTxAmount: 0,
        excessiveVolumeIncreasePercent1vs90: 0,
        excessiveVolumeIncreaseAmount1vs90: 0,
        excessiveVolumeIncreasePercent1vs30: 0,
        excessiveVolumeIncreaseAmount1vs30: 0,
        excessiveVolumeIncreasePercent1vs7: 0,
        excessiveVolumeIncreaseAmount1vs7: 0,
        excessiveVolumeIncreasePercent7vs90: 0,
        excessiveVolumeIncreaseAmount7vs90: 0,
        dailyUnmatchedCreditsCount: 0,
        dailyUnmatchedCreditsAmount: 0,
        sameCardTxCount: 0,
        sameCardTxAmount: 0,
        totalDailyForeignCardCount: 0,
        totalDailyForeignCardTxAmount: 0,
        txLimitToAlert: 0,
        singleForcedTxAmount: 0,
        aggregateForcedTxAmount: 0,
        staleTxDays: 0
      },
      merchantContractedRatesQueue: null as any,
      statementOptions: null as any,
      remitToAddress: null as any,
      clientName: "",
      siteId: "",
      practiceId: "",
      practiceName: "",
      enterpriseId: "",
      enterprisePracticeId: "",
      gcn: ""
    };
    this.createMerchant = this.fb.group(
      {
        processor: new FormControl(this.vendorType.INSTAMED, [Validators.required]),
        generalInfo: this.fb.group({
          merchantName: ["", [Validators.required, Validators.maxLength(50)]],
          externalMerchantId: ["", [Validators.maxLength(12), Validators.min(0)]],
          customerAccountNumber: ["", Validators.required],
          instaMedConversionStatus: [false],
          previousMMID: ["", [Validators.pattern(PATTERN_CONSTANTS.NUMBER), Validators.minLength(10)]],
          suppressZeroDollarPayment: [true],
          achEcheckEnabled: [true],
          amexEnabled: [false],
          ngepmStatement: [true],
          balanceIncludesBadDebt: [false]
        }),
        instamedFormGroup: this.fb.group({
          merchantId: ["", Validators.required],
          clientId: [""],
          clientSecret: [""],
          storeId: ["", [Validators.required, Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]],
          accountId: [""],
          terminalInfo: this.fb.group({
            patientPortal: ["", [Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]],
            virtualVisits: ["", [Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]],
            engage: ["", [Validators.pattern(PATTERN_CONSTANTS.NUMBER_4), Validators.maxLength(4)]]
          })
        }),
        globalPaymentsFormGroup: this.fb.group({
          globalPaymentsMerchantName: ["", [requiredTrimmed, Validators.maxLength(100)]],
          globalPaymentsSiteId: ["", [requiredTrimmed, Validators.maxLength(50)]],
          globalPaymentsKey: ["", [requiredTrimmed, Validators.maxLength(50)]]
        }),
        acceptedCards: this.fb.group({}, { validators: minSelected(0) }),
        accountDetails: this.fb.group({
          federalTaxId: [""],
          accountUsage: [""],
          accountType: [""],
          checkingDepositType: [""],
          routingNumber: [""],
          accountNumber: [""]
        }),
        otherAccountDetails: this.fb.group({
          accountUsage: ["FeeWithdrawals"],
          accountType: [""],
          routingNumber: ["", [Validators.pattern("^([0-9]{9})$")]],
          accountNumber: ["", [Validators.pattern("[0-9]+$"), Validators.maxLength(49)]],
          checkingDepositType: [""]
        })
      },
      { validators: accountNumberValidation() }
    );
    this.merchant.accountDetails.preferredProcessor = this.vendorType.INSTAMED;
    this.sharedFormService.addCheckboxes(this.acceptedCardsFormGroup, this.merchant);
    this.vendorTypeChange();
  }

  get form() {
    return this.createMerchant.controls;
  }

  get generalInfoFormGroup(): FormGroup {
    return this.createMerchant.controls["generalInfo"] as FormGroup;
  }

  get contactsFormGroup(): FormGroup {
    return this.createMerchant.controls["contacts"] as FormGroup;
  }

  get merchantAddressFormGroup(): FormGroup {
    return this.createMerchant.controls["merchantAddress"] as FormGroup;
  }

  get remitAddressFormGroup(): FormGroup {
    return this.createMerchant.controls["remitToAddress"] as FormGroup;
  }

  get legalEntityFormGroup(): FormGroup {
    return this.createMerchant.controls["legalEntity"] as FormGroup;
  }

  get beneficialOwnersFormArray(): FormArray {
    return this.createMerchant.controls["principals"] as FormArray;
  }

  get acceptedCardsFormGroup(): FormGroup {
    return this.createMerchant.controls["acceptedCards"] as FormGroup;
  }

  get instamedFormGroup(): FormGroup {
    return this.createMerchant.controls["instamedFormGroup"] as FormGroup;
  }

  get globalPaymentsFormGroup(): FormGroup {
    return this.createMerchant.controls["globalPaymentsFormGroup"] as FormGroup;
  }

  get terminalIdGroup(): FormGroup {
    return this.instamedFormGroup?.controls.terminalInfo as FormGroup;
  }

  get paypalAccountDetailsFormGroup(): FormGroup {
    return this.createMerchant.controls["paypalAccountDetailsFormGroup"] as FormGroup;
  }

  get cardNotPresentPasswordGroupControls() {
    const group = this.paypalAccountDetailsFormGroup.get("cardNotPresentPasswordGroup") as FormGroup;
    return group.controls;
  }

  get cardPresentPasswordGroupControls() {
    const group = this.paypalAccountDetailsFormGroup.get("cardPresentPasswordGroup") as FormGroup;
    return group.controls;
  }

  get accountDetailsFormGroup(): FormGroup {
    return this.createMerchant.controls["accountDetails"] as FormGroup;
  }

  get otherAccountDetailsFormGroup(): FormGroup {
    return this.createMerchant.controls["otherAccountDetails"] as FormGroup;
  }

  isPaypal(): boolean {
    return this.merchant.accountDetails.preferredProcessor?.toLowerCase() === MERCHANT_CONSTANTS.PAYPAL.toLowerCase();
  }

  ownerForm(id: number, ownerType: string): FormGroup {
    return new FormGroup({
      beneficialOwnerId: new FormControl(id),
      firstName: new FormControl("", [Validators.required]),
      lastName: new FormControl("", [Validators.required]),
      title: new FormControl(""),
      addressLine1: new FormControl("", [Validators.required, Validators.maxLength(99)]),
      addressLine2: new FormControl("", [Validators.maxLength(99)]),
      city: new FormControl("", [Validators.required, Validators.maxLength(49)]),
      state: new FormControl("", [Validators.required]),
      zipCode: new FormControl("", [
        Validators.required,
        Validators.pattern(PATTERN_CONSTANTS.NUMBER),
        Validators.maxLength(11)
      ]),
      country: new FormControl("US", [Validators.required]),
      phoneNumber: new FormControl("", [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10), Validators.maxLength(10)]),
      ssn: new FormControl("", [Validators.pattern("^([0-9]{9})$")]),
      percentOwned: new FormControl("", [
        Validators.required,
        Validators.pattern(PATTERN_CONSTANTS.PERCENT_3_DECIMALS),
        Validators.max(100),
        Validators.min(0)
      ]),
      dateOfBirth: new FormControl(""),
      email: new FormControl(""),
      ownerType: new FormControl(ownerType),
      contactType: new FormControl("")
    });
  }

  getbeneficialOwnersFormGroup(index: number): FormGroup {
    return this.beneficialOwnersFormArray.controls[index] as FormGroup;
  }

  addBeneficialOwner(): void {
    this.beneficialOwnersFormArray.push(
      this.ownerForm(this.beneficialOwnersFormArray.length, "Beneficial Owner " + this.beneficialOwnersFormArray.length)
    );
  }

  removeBeneficialOwner(owner: AbstractControl): void {
    for (let currentIndex = 0; currentIndex < this.beneficialOwnersFormArray.length; currentIndex++) {
      if (this.beneficialOwnersFormArray?.value[currentIndex].beneficialOwnerId === owner.value.beneficialOwnerId) {
        this.beneficialOwnersFormArray.removeAt(currentIndex);
      }
    }
  }

  showAddBeneficialOwner(owner?: AbstractControl): any {
    return (
      (this.beneficialOwnersFormArray.length === 0 ||
        (owner &&
          owner?.value?.beneficialOwnerId ===
            this.beneficialOwnersFormArray?.value[this.beneficialOwnersFormArray.length - 1].beneficialOwnerId &&
          this.beneficialOwnersFormArray.length <= 10)) &&
      !(
        this.generalInfoFormGroup.controls.ownershipType &&
        this.generalInfoFormGroup.controls.ownershipType.value === "SoleProprietorship"
      )
    );
  }

  showRemoveBeneficialOwner(owner: AbstractControl): boolean {
    return (
      owner?.value?.beneficialOwnerId !== this.beneficialOwnersFormArray?.value[0].beneficialOwnerId ||
      this.showAddBeneficialOwnerWithoutField()
    );
  }

  showAddBeneficialOwnerWithoutField(): boolean {
    this.ownerships = ["Government", "FinancialInstitution", "Trust", "PublicCorporation", "SECRegulatedCorporation"];
    return (
      this.generalInfoFormGroup.controls.ownershipType &&
      this.ownerships.includes(this.generalInfoFormGroup.controls.ownershipType.value)
    );
  }

  hasRemitEvent(hasRemitEvent: boolean) {
    this.hasRemit = hasRemitEvent;
    if (this.hasRemit) {
      this.remitAddressFormGroup.controls["state"].setValue("");
      this.remitAddressFormGroup.controls["country"].setValue("");
    }
  }

  updateFormGroupValidity(): void {
    Object.keys(this.createMerchant.controls).forEach((key: string) => {
      const abstractControl = this.createMerchant.controls[key];
      if (abstractControl instanceof FormGroup) {
        Object.keys(abstractControl.controls).forEach((key: string) => {
          const childControl = abstractControl.controls[key];
          childControl.updateValueAndValidity();
        });
      } else {
        abstractControl.updateValueAndValidity();
      }
    });
  }

  vendorTypeChange(): void {
    this.merchant.accountDetails.preferredProcessor = this.form.processor.value;
    this.accountDetailsFormGroup.controls["federalTaxId"].removeValidators(Validators.required);
    this.accountDetailsFormGroup.controls["accountUsage"].removeValidators(Validators.required);
    this.accountDetailsFormGroup.controls["accountType"].removeValidators(Validators.required);
    this.accountDetailsFormGroup.controls["routingNumber"].removeValidators(Validators.required);
    this.accountDetailsFormGroup.controls["accountNumber"].removeValidators(Validators.required);
    this.acceptedCardsFormGroup.controls["MCARD"]?.enable();
    this.acceptedCardsFormGroup.controls["VISA"]?.enable();

    if (this.merchantService.isInstaMed(this.merchant)) {
      if (this.terminalIdGroup) {
        this.terminalIdGroup.setValidators(minSelected(1));
      }
      this.instamedFormGroup.enable();
    } else {
      this.instamedFormGroup.reset();
      if (this.terminalIdGroup) {
        this.terminalIdGroup.clearValidators();
      }
      this.instamedFormGroup.disable();
    }

    if (this.merchantService.isGlobalPayments(this.merchant)) {
      // Global Payments merchants always have AMEX enabled and ACH/eCheck disabled
      this.generalInfoFormGroup.patchValue({ achEcheckEnabled: false, amexEnabled: true });
      this.generalInfoFormGroup.controls.achEcheckEnabled.disable();
      this.generalInfoFormGroup.controls.amexEnabled.disable();
      this.globalPaymentsFormGroup.enable();
    } else {
      this.generalInfoFormGroup.patchValue({ achEcheckEnabled: true, amexEnabled: false });
      this.generalInfoFormGroup.controls.achEcheckEnabled.enable();
      this.generalInfoFormGroup.controls.amexEnabled.enable();
      this.globalPaymentsFormGroup.reset();
      this.globalPaymentsFormGroup.disable();
    }
    this.updateFormGroupValidity();
  }

  private applyCommonProcessorDetails(tempMerchant: Merchant): void {
    tempMerchant.suppressZeroBalancePayment = this.generalInfoFormGroup.controls.suppressZeroDollarPayment.value;
    tempMerchant.achEcheckEnabled = this.generalInfoFormGroup.controls.achEcheckEnabled.value;
    tempMerchant.amexEnabled = this.generalInfoFormGroup.controls.amexEnabled.value;
    tempMerchant.ngepmStatement = this.generalInfoFormGroup.controls.ngepmStatement.value;
    tempMerchant.practiceName = this.generalInfoFormGroup.controls.practiceNameSearch.value;
    tempMerchant.siteId = this.generalInfoFormGroup.controls.siteId.value;
    tempMerchant.practiceId = this.generalInfoFormGroup.controls.enterprisePracticeId.value;
    tempMerchant.clientName = this.generalInfoFormGroup.controls.clientName.value;
    tempMerchant.enterpriseId = this.generalInfoFormGroup.controls.enterpriseId.value;
    tempMerchant.enterprisePracticeId = this.generalInfoFormGroup.controls.enterprisePracticeId.value;
    tempMerchant.gcn = this.generalInfoFormGroup.controls.gcn.value;
    tempMerchant.locationId = this.generalInfoFormGroup.controls.locationId?.value;
    tempMerchant.locationName = this.generalInfoFormGroup.controls.locationName?.value;
    tempMerchant.locationAlias = this.generalInfoFormGroup.controls.locationAlias?.value;
    tempMerchant.balanceIncludesBadDebt = this.generalInfoFormGroup.controls.balanceIncludesBadDebt.value;
  }

  saveMerchant(): void {
    const tempMerchant: Merchant = JSON.parse(JSON.stringify(this.merchant));
    this.sharedFormService.updateMerchantGeneralInfo(tempMerchant, this.generalInfoFormGroup);
    tempMerchant.accountDetails.separateFundingAccounts = null as any;
    tempMerchant.accountDetails.worldPayAccountDetails = null as any;
    tempMerchant.accountDetails.feeAccountDetails = null as any;
    this.otherAccountDetailsFormGroup.controls["accountNumber"].setValue(0);
    this.applyCommonProcessorDetails(tempMerchant);

    if (this.merchantService.isInstaMed(tempMerchant)) {
      tempMerchant.accountDetails.paypalAccountDetails = null as any;
      delete tempMerchant.accountDetails.globalPaymentsAccountDetails;
      tempMerchant.accountDetails.instaMedAccountDetails = {
        clientId: this.instamedFormGroup.controls.clientId.value,
        clientSecret: this.instamedFormGroup.controls.clientSecret.value,
        instaMedTerminalIDList: {
          PATIENT_PORTAL: this.terminalIdGroup.controls.patientPortal.value,
          VIRTUAL_VISITS: this.terminalIdGroup.controls.virtualVisits.value,
          ENGAGE: this.terminalIdGroup.controls.engage.value
        },
        merchantId: this.instamedFormGroup.controls.merchantId.value,
        storeId: this.instamedFormGroup.controls.storeId.value,
        accountId: this.instamedFormGroup.controls.accountId.value
      };
      tempMerchant.merchantMigrationDetails = {
        toBeMigrated: this.generalInfoFormGroup.controls.instaMedConversionStatus.value,
        effectiveDate: null,
        previousMmid: Number(this.generalInfoFormGroup.controls.previousMMID.value)
      };
    } else if (this.merchantService.isGlobalPayments(tempMerchant)) {
      tempMerchant.accountDetails.paypalAccountDetails = null as any;
      tempMerchant.accountDetails.instaMedAccountDetails = null as any;
      tempMerchant.accountDetails.globalPaymentsAccountDetails = {
        globalPaymentsMerchantName: this.globalPaymentsFormGroup.controls.globalPaymentsMerchantName.value.trim(),
        globalPaymentsSiteId: this.globalPaymentsFormGroup.controls.globalPaymentsSiteId.value.trim(),
        globalPaymentsKey: this.globalPaymentsFormGroup.controls.globalPaymentsKey.value.trim()
      };
      tempMerchant.merchantMigrationDetails = null;
    }
    this.createMerchant.markAllAsTouched();
    if (this.createMerchant.valid) {
      if (
        this.generalInfoFormGroup.controls.instaMedConversionStatus.value &&
        this.merchantService.isInstaMed(tempMerchant)
      ) {
        // calling the api to get merchant if user haven't clicked on get Merchant Info after entering previousMmid
        if (!this.merchantService?.merchantMigrationDetails?.previousMmid) {
          this.merchantService
            .getPreviousMerchantDetails(tempMerchant.merchantMigrationDetails?.previousMmid as number)
            .subscribe(
              (merchantDetails) => {
                this.merchantService.merchantMigrationDetails = merchantDetails;
                if (this.isCreateMerchantValidForMigration()) {
                  this.displayConfirmationModalOptions(merchantDetails, tempMerchant);
                }
              },
              (error) => {
                this.notificationService.error(error);
              }
            );
        } else {
          if (this.isCreateMerchantValidForMigration()) {
            this.displayConfirmationModalOptions(this.merchantService.merchantMigrationDetails, tempMerchant);
          }
        }
      } else {
        this.createMerchantApi(tempMerchant);
      }
    } else {
      this.beneficialOwnersFormArray?.controls?.[0]?.get("ownerType")?.setValue("Control Owner");
    }
  }

  isCreateMerchantValidForMigration(): boolean {
    return (
      !this.generalInfoFormGroup.controls.instaMedConversionStatus.value ||
      (this.generalInfoFormGroup.controls.instaMedConversionStatus.value &&
        this.merchantService.isPracticeIdMatched(this.generalInfoFormGroup.controls.externalMerchantId.value))
    );
  }

  displayConfirmationModalOptions(migrationDetails: MerchantMigrationDetails, tempMerchant: Merchant) {
    const confirmationModalOptions: ModalOptions = {
      backdrop: "static",
      keyboard: false,
      initialState: {
        merchantMigrationInfo: migrationDetails
      }
    };
    this.modalRef = this.modalService.show(ConfirmationModalComponent, confirmationModalOptions);
    this.modalRef.content.onClose.subscribe((result: any) => {
      if (result) {
        this.createMerchantApi(tempMerchant);
      }
    });
  }

  createMerchantApi(tempMerchant: Merchant): void {
    this.actionInProgress = true;
    this.merchantService.createMerchants(tempMerchant).subscribe(
      (createdMerchant) => {
        this.actionInProgress = false;
        this.router.navigate(["/merchants", createdMerchant.id]);
      },
      (error) => {
        this.actionInProgress = false;
        this.notificationService.error(error.error);
      }
    );
  }
}
